import React, { useEffect, useState, useMemo } from "react";
import LoginChecked from "../Shared/LoginChecked";
import ProductDropDown from "../Shared/ProductsDropDown";
import {
  Form,
  Select,
  Table,
  Button,
  Row,
  Col,
  message,
  Pagination,
  Alert,
  Tag,
  Checkbox,
  Modal,
  Tooltip,
  Space,
} from "antd";
import BoxCard from "../Shared/BoxCard";
import axios from "axios";
import config from "../../config";
import dayjs from "dayjs";
import { DownloadOutlined, ToolOutlined, EditOutlined } from "@ant-design/icons";
import { NavLink } from "react-router-dom";

function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(2);
}

function escapeExcelCell(value) {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadExcelFile(rows, fileName) {
  if (!rows || rows.length === 0) return false;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.map(escapeExcelCell).join(","),
    ...rows.map((row) =>
      headers.map((h) => escapeExcelCell(row[h])).join(",")
    ),
  ];
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

function mapRowsToExcel(rows) {
  if (!rows || rows.length === 0) return [];
  return rows.map((item, index) => {
    const isSale = item.TransactionType === "Sale";
    const sale = Number(item.Sale_Rate) || 0;
    const cost = Number(item.AVGPurchaseRate) || 0;
    const qty = Number(item.Quantity) || 0;
    return {
      Sr: index + 1,
      Product: item.ProductName || "",
      Date: item.Dated ? dayjs(item.Dated).format(config.date_format) : "",
      "Line ID": item.TransactionID,
      Type: item.TransactionType || "",
      Qty: item.Quantity,
      "Remaining qty": item.RemainingQTY,
      "Purchase rate (line)": item.Purchase_Rate,
      "Weighted avg cost": item.AVGPurchaseRate,
      "Sale rate": item.Sale_Rate,
      "Stored cost on sale": item.ExistingPurchaseRateinSaleTable,
      "Est. line profit": isSale ? Number(((sale - cost) * qty).toFixed(2)) : "",
      Status: item.Status || "",
    };
  });
}

function RateAdjustForIncome() {
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const [loading, setLoading] = useState(false);
  const [ProductID, setProductID] = useState(0);
  const [ReportLoading, setReportLoading] = useState(false);
  const [ReportData, setReportData] = useState([]);
  const ProductType = ProductDropDown();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasNegativeStock, setHasNegativeStock] = useState(false);
  const [canUpdateRates, setCanUpdateRates] = useState(false);
  const [negativeStockCount, setNegativeStockCount] = useState(0);
  const [showOnlyProblematic, setShowOnlyProblematic] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [fixingRowKey, setFixingRowKey] = useState(null);

  const productOptions = useMemo(() => {
    const all = { value: 0, label: "All products" };
    if (!ProductType || ProductType === false) return [all];
    return [all, ...ProductType];
  }, [ProductType]);

  useEffect(() => {
    document.title = "Purchase rate adjustment (weighted average)";
  }, []);

  const buildPayload = (overrides = {}) => ({
    UserID: UserID,
    AccessKey: AccessKey,
    ProductID: ProductID,
    PageNo: pageNo,
    PageSize: pageSize,
    ShowOnlyNegativeStock: showOnlyProblematic,
    ...overrides,
  });

  const fetchAdjustmentReport = () => {
    setReportLoading(true);

    var api_config = {
      method: "post",
      url: config.base_url + "Product/GetAdjustmentReport",
      headers: {
        "Content-Type": "application/json",
      },
      data: buildPayload(),
    };

    axios(api_config)
      .then((response) => {
        if (response.data.status_code === 1) {
          const rows = response.data.listofRateDetails || [];
          setReportData(rows);
          setTotalRecords(response.data.totalRecords ?? 0);
          setHasNegativeStock(!!response.data.hasNegativeStock);
          setCanUpdateRates(!!response.data.canUpdateRates);
          setNegativeStockCount(response.data.negativeStockCount ?? 0);
        } else {
          setReportData([]);
          setTotalRecords(0);
          setHasNegativeStock(false);
          setCanUpdateRates(false);
          setNegativeStockCount(0);
          message.error(
            response.data.status_message || "Could not load adjustment report."
          );
        }
      })
      .catch((error) => {
        console.error("API call failed:", error);
        message.error("Network error while loading the report.");
      })
      .finally(() => {
        setReportLoading(false);
      });
  };

  useEffect(() => {
    if (!UserID || !AccessKey) return;
    fetchAdjustmentReport();
  }, [pageNo, pageSize, ProductID, showOnlyProblematic]);

  /** Fetch every matching row (not just the current page) for Excel. */
  const handleExportAllToExcel = () => {
    setExportLoading(true);
    const exportPageSize = Math.max(totalRecords || 0, 1);

    axios({
      method: "post",
      url: config.base_url + "Product/GetAdjustmentReport",
      headers: { "Content-Type": "application/json" },
      data: buildPayload({
        PageNo: 1,
        PageSize: exportPageSize > 1 ? exportPageSize : 500000,
      }),
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          const rows = response.data.listofRateDetails || [];
          const excelRows = mapRowsToExcel(rows);
          if (excelRows.length === 0) {
            message.warning("No rows to export.");
            return;
          }
          const fileName = `Purchase_Rate_Adjustment_${dayjs().format(
            "DD-MM-YYYY"
          )}${showOnlyProblematic ? "_NegativeStock" : ""}`;
          downloadExcelFile(excelRows, fileName);
          message.success(`Exported ${excelRows.length} row(s) to Excel.`);
        } else {
          message.error(
            response.data.status_message || "Could not export report."
          );
        }
      })
      .catch(() => {
        message.error("Network error while exporting.");
      })
      .finally(() => {
        setExportLoading(false);
      });
  };

  const handelSubmit = () => {
    if (ProductID <= 0) {
      message.warning("Select a single product before updating purchase rates.");
      return;
    }
    if (!canUpdateRates || hasNegativeStock) {
      message.warning(
        "Fix negative stock (add purchases before sales), then refresh the report before updating."
      );
      return;
    }

    setLoading(true);
    const payload = {
      UserID: UserID,
      AccessKey: AccessKey,
      ProductID: ProductID,
    };

    var api_config = {
      method: "post",
      url: config.base_url + "Product/AdjustRatesforProfit",
      headers: {
        "Content-Type": "application/json",
      },
      data: payload,
    };
    axios(api_config)
      .then((response) => {
        if (response.data.status_code === 1) {
          message.success(
            response.data.status_message || "Purchase rates updated."
          );
          fetchAdjustmentReport();
        } else {
          message.error(
            response.data.status_message || "Failed to update purchase rates."
          );
        }
      })
      .catch((error) => {
        console.log(error);
        message.error("An error occurred while updating purchase rates.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFixNegativeStock = (record) => {
    if (!record?.CanFixNegativeStock || !record?.NextPurchaseHeadID) {
      message.warning(
        "No later purchase invoice found after this sale. Edit the next purchase date manually, or add a purchase before the sale."
      );
      return;
    }

    const saleDate = dayjs(record.Dated).format(config.date_format);
    const nextDate = record.NextPurchaseDated
      ? dayjs(record.NextPurchaseDated).format(config.date_format)
      : "—";
    const invLabel = record.NextPurchaseInvoiceNo
      ? `#${record.NextPurchaseInvoiceNo}`
      : `ID ${record.NextPurchaseHeadID}`;

    Modal.confirm({
      title: "Move next purchase before this sale?",
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            Sale on <strong>{saleDate}</strong> has negative stock. The next
            purchase {invLabel} is dated <strong>{nextDate}</strong>.
          </p>
          <p style={{ marginBottom: 0 }}>
            This will set that purchase date to <strong>{saleDate}</strong>{" "}
            (same day as the sale) and update matching ledger dates, so the
            purchase sorts before the sale.
          </p>
        </div>
      ),
      okText: "Move purchase date",
      cancelText: "Cancel",
      onOk: () => {
        const rowKey = `${record.TransactionID}-${record.NextPurchaseHeadID}`;
        setFixingRowKey(rowKey);
        return axios({
          method: "post",
          url: config.base_url + "Product/FixNegativeStockPurchaseDate",
          headers: { "Content-Type": "application/json" },
          data: {
            UserID,
            AccessKey,
            ID: record.NextPurchaseHeadID,
            Dated: record.Dated,
            ProductID: record.ProductID || ProductID || 0,
          },
        })
          .then((response) => {
            if (response.data.status_code === 1) {
              message.success(
                response.data.status_message || "Purchase date updated."
              );
              fetchAdjustmentReport();
            } else {
              message.error(
                response.data.status_message || "Could not move purchase date."
              );
            }
          })
          .catch(() => {
            message.error("Network error while fixing purchase date.");
          })
          .finally(() => {
            setFixingRowKey(null);
          });
      },
    });
  };

  const renderStatusTag = (status) => {
    const s = (status || "").trim();
    if (!s) return "—";
    const lower = s.toLowerCase();
    if (lower === "ok") return <Tag color="success">{status}</Tag>;
    if (lower === "negative stock")
      return <Tag color="error">{status}</Tag>;
    if (lower === "needs update")
      return <Tag color="warning">{status}</Tag>;
    if (lower === "stock in") return <Tag color="default">{status}</Tag>;
    if (lower.indexOf("dip") >= 0)
      return <Tag color="processing">{status}</Tag>;
    return <Tag>{status}</Tag>;
  };

  const columns = [
    {
      title: "Sr. #",
      dataIndex: "Sr",
      key: "Sr",
      width: 72,
      render: (_, record, index) => index + 1 + pageSize * (pageNo - 1),
    },
    {
      title: "Product",
      dataIndex: "ProductName",
      key: "productname",
    },
    {
      title: "Date",
      dataIndex: "Dated",
      key: "dated",
      render: (_, record) => dayjs(record.Dated).format(config.date_format),
    },
    {
      title: "Line ID",
      dataIndex: "TransactionID",
      key: "transectionid",
    },
    {
      title: "Type",
      dataIndex: "TransactionType",
      key: "transactiontype",
    },
    {
      title: "Qty",
      dataIndex: "Quantity",
      key: "quantity",
    },
    {
      title: "Remaining qty",
      dataIndex: "RemainingQTY",
      key: "remainingqty",
      render: (v) => formatMoney(v),
    },
    {
      title: "Purchase rate (line)",
      dataIndex: "Purchase_Rate",
      key: "purchase_rate",
      render: (value) => formatMoney(value),
    },
    {
      title: "Weighted avg cost",
      dataIndex: "AVGPurchaseRate",
      key: "avgpurchaserate",
      render: (value) => formatMoney(value),
    },
    {
      title: "Sale rate",
      dataIndex: "Sale_Rate",
      key: "sale_rate",
      render: (value) => formatMoney(value),
    },
    {
      title: "Stored cost on sale",
      dataIndex: "ExistingPurchaseRateinSaleTable",
      key: "existingpurchaserateinsaletable",
      render: (value) => formatMoney(value),
    },
    {
      title: "Est. line profit",
      key: "lineprofit",
      render: (_, record) => {
        if (record.TransactionType !== "Sale") return "—";
        const sale = Number(record.Sale_Rate) || 0;
        const cost = Number(record.AVGPurchaseRate) || 0;
        const qty = Number(record.Quantity) || 0;
        return formatMoney((sale - cost) * qty);
      },
    },
    {
      title: "Status / remark",
      dataIndex: "Status",
      key: "status",
      width: 160,
      render: (value) => renderStatusTag(value),
    },
    {
      title: "Fix",
      key: "fix",
      fixed: "right",
      width: 260,
      render: (_, record) => {
        const isNeg =
          (record.Status || "").toLowerCase() === "negative stock";
        if (!isNeg) return "—";

        if (!record.CanFixNegativeStock) {
          return (
            <Tooltip title="No later purchase invoice found after this sale. Edit purchase/DIP dates manually.">
              <span style={{ color: "#94a3b8", fontSize: 12 }}>No auto-fix</span>
            </Tooltip>
          );
        }

        const nextDate = record.NextPurchaseDated
          ? dayjs(record.NextPurchaseDated).format(config.date_format)
          : "—";
        const inv = record.NextPurchaseInvoiceNo
          ? `#${record.NextPurchaseInvoiceNo}`
          : `ID ${record.NextPurchaseHeadID}`;
        const rowKey = `${record.TransactionID}-${record.NextPurchaseHeadID}`;

        return (
          <Space size={4} wrap>
            <Tooltip
              title={`Move purchase ${inv} from ${nextDate} to this sale date`}
            >
              <Button
                size="small"
                type="primary"
                danger
                icon={<ToolOutlined />}
                loading={fixingRowKey === rowKey}
                onClick={() => handleFixNegativeStock(record)}
              >
                Move next purchase
              </Button>
            </Tooltip>
            <Tooltip title={`Open purchase ${inv} in edit`}>
              <NavLink
                to={`/purchase/edit-purchase/${record.NextPurchaseHeadID}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="small" icon={<EditOutlined />}>
                  Edit
                </Button>
              </NavLink>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const onPageChange = (page) => {
    setPageNo(page);
  };

  const onPageSizeChange = (current, size) => {
    setPageNo(1);
    setPageSize(size);
  };

  const onProductChange = (value) => {
    setPageNo(1);
    setProductID(value);
  };

  const onProblematicFilterChange = (e) => {
    setPageNo(1);
    setShowOnlyProblematic(e.target.checked);
  };

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">
              Purchase rate adjustment (weighted average cost)
            </h3>
            <div className="header-actions">
              <Button
                type="dashed"
                shape="round"
                size="large"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                disabled={totalRecords <= 0}
                onClick={handleExportAllToExcel}
              >
                Export to Excel
              </Button>
            </div>
          </div>
          <div className="card-total-detal">
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col md={24}>
                <BoxCard
                  title={
                    <p style={{ marginBottom: 0 }}>
                      <strong>
                        Cost on each sale line is the moving{" "}
                        <em>weighted average</em> of purchase and DIP-adjustment
                        rates up to that date (back-dated entries are
                        supported). Use this screen to align stored purchase cost
                        on sale lines with that average so profit reports are
                        consistent. Enter purchases (or DIP gains) before sales
                        so stock never goes negative—otherwise updates are
                        blocked.
                      </strong>
                    </p>
                  }
                />
              </Col>
            </Row>
          </div>

          {hasNegativeStock && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Negative stock on one or more rows${
                negativeStockCount > 0 ? ` (${negativeStockCount})` : ""
              }`}
              description={
                <>
                  A sale exceeds available quantity at that point in time. Use{" "}
                  <strong>Move next purchase</strong> on a problematic row to set
                  the next purchase invoice date to the sale date (same day)—this
                  usually clears the temporary negative stock. You can still edit
                  purchase dates manually. Refresh afterwards; rate updates stay
                  disabled until negative stock is gone. Use{" "}
                  <strong>Show only problematic rows</strong> to focus on errors.
                </>
              }
            />
          )}

          {!hasNegativeStock && ProductID > 0 && canUpdateRates && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Ready to update"
              description="Stored purchase cost on sale lines matches the computed average where shown as OK, or you can overwrite with the latest weighted average for lines marked Needs update."
            />
          )}

          {ProductID === 0 && !hasNegativeStock && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Viewing all products"
              description="Select one product to enable “Apply weighted average to sale lines” for that product only."
            />
          )}

          <div className="filters-wrap">
            <Form layout="inline">
              <Form.Item label="Product">
                <Select
                  showSearch
                  style={{ minWidth: 240 }}
                  placeholder="All products"
                  optionFilterProp="label"
                  value={ProductID}
                  onChange={onProductChange}
                  options={productOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={showOnlyProblematic}
                  onChange={onProblematicFilterChange}
                >
                  Show only problematic rows
                  {negativeStockCount > 0 ? ` (${negativeStockCount})` : ""}
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Button
                  type="default"
                  size="medium"
                  shape="round"
                  onClick={fetchAdjustmentReport}
                  loading={ReportLoading}
                >
                  Refresh report
                </Button>
              </Form.Item>
            </Form>
          </div>

          <Table
            size="small"
            scroll={{ x: "max-content" }}
            loading={ReportLoading}
            columns={columns}
            dataSource={ReportData}
            rowKey={(row) =>
              `${row.ProductName}-${row.TransactionType}-${row.TransactionID}-${row.Dated}`
            }
            pagination={false}
            bordered
            rowClassName={(record) =>
              (record.Status || "").toLowerCase() === "negative stock"
                ? "row-negative-stock"
                : ""
            }
          />
          <div className="text-end mt-3">
            <Pagination
              current={pageNo}
              pageSize={pageSize}
              size="small"
              total={totalRecords}
              onChange={onPageChange}
              onShowSizeChange={onPageSizeChange}
              showSizeChanger
              showTotal={(total) => `Total ${total} rows`}
            />
          </div>
          <div
            className="header-actions"
            style={{
              marginTop: "20px",
              marginRight: "60px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="primary"
              size="large"
              shape="round"
              loading={loading}
              disabled={
                ProductID <= 0 || !canUpdateRates || hasNegativeStock
              }
              onClick={handelSubmit}
            >
              Apply weighted average to sale lines
            </Button>
          </div>
        </div>
      </div>
      <style>{`
        .row-negative-stock > td {
          background: #fff1f0 !important;
        }
      `}</style>
    </>
  );
}

export default RateAdjustForIncome;
